import moment from 'moment'
import { IdCardType } from './enums'
import utils from './utils'
import _ from 'lodash'

const idCardRules = {
  idCard (val) {
    return utils.isEmptyString(val) || utils.validateIdCard(val) || '请输入合法的身份证号码'
  },
  idCardHm (val) {
    return utils.isEmptyString(val) || utils.validateIdCardHm(val) || '请输入合法的港澳通行证号码'
  },
  idCardTw (val) {
    return utils.isEmptyString(val) || utils.validateIdCardTw(val) || '请输入合法的港澳通行证号码'
  },
  idCardPassport (val) {
    return utils.isEmptyString(val) || utils.validateIdCardPassport(val) || '请输入合法的护照号码'
  },
  idCardOthers (val) {
    return utils.isEmptyString(val) || utils.validateIdCardOthers(val) || '请输入合法的证件号码'
  }
}

export default Object.freeze({
  empty (val) {
    return true
  },
  or (...rules) {
    if (!rules.length) return true
    const tailItem = rules[rules.length - 1]
    const useErrMsg = typeof tailItem !== 'function'
    return function (val) {
      let result = true
      for (
        let index = 0, length = useErrMsg ? rules.length - 1 : rules.length;
        index < length;
        index++
      ) {
        const rule = rules[index]
        result = rule(val)
        if (result === true) {
          return true
        }
      }
      return useErrMsg ? tailItem : result
    }
  },

  and (...rules) {
    if (!rules.length) return true
    const tailItem = rules[rules.length - 1]
    const useErrMsg = typeof tailItem !== 'function'
    return function (val) {
      for (
        let index = 0, length = useErrMsg ? rules.length - 1 : rules.length;
        index < length;
        index++
      ) {
        const rule = rules[index]
        const result = rule(val)
        if (result !== true) {
          return useErrMsg ? tailItem : result
        }
      }
      return true
    }
  },

  required (val) {
    return utils.isNotEmptyString(val) || '* 必填'
  },
  requiredAllWith (...others) {
    return function (val) {
      return (utils.isNotEmptyString(val) && (!others?.length || others.every(utils.isNotEmptyString))) || '* 必填'
    }
  },
  requiredAnyWith (...others) {
    return function (val) {
      return (utils.isNotEmptyString(val) || (others?.length && others.some(utils.isNotEmptyString))) || '* 必填'
    }
  },
  notWhitespace (val) {
    return utils.isNotWhitespaceString(val) || '* 必填'
  },
  ...idCardRules,
  idCardWith (idCardType) {
    switch (idCardType) {
      case IdCardType.Hm: return idCardRules.idCardHm
      case IdCardType.Tw: return idCardRules.idCardTw
      case IdCardType.Passport: return idCardRules.idCardPassport
      case IdCardType.Army: return idCardRules.idCardOthers
      default: return idCardRules.idCard
    }
  },
  donNo (val) {
    return utils.isEmptyString(val) || /^[0-9A-Z]+$/.test(val) || '请输入合法的献血码'
  },
  donatorNo (val) {
    return utils.isEmptyString(val) || /^[0-9A-Z]+$/.test(val) || '请输入合法的档案编号'
  },
  datetimeWith (format) {
    return function (val) {
      return (
        utils.isEmptyString(val) ||
        val instanceof Date ||
        moment(val, format, true).isValid() ||
        '请输入合法的日期时间'
      )
    }
  },
  rangeInt (min, max, excludeMin, excludeMax) {
    if (min && typeof (min) === 'object' && !(min instanceof Number)) {
      const options = min
      min = options.min
      max = options.max
      excludeMin = options.excludeMin
      excludeMax = options.excludeMax
    }
    if (!_.isNaN(min) && Number.isNaN(min)) {
      throw new Error('指定的整数范围最小值无效:' + min)
    }
    if (!_.isNaN(max) && Number.isNaN(max)) {
      throw new Error('指定的整数范围最大值无效:' + max)
    }
    return function (val) {
      if (utils.isEmptyString(val)) return true
      const num = Number(val)
      if (Number.isNaN(num) || !Number.isInteger(num)) {
        return `输入值(${val})不是有效整数`
      }
      return ((
        (_.isNil(min) || (excludeMin ? num > min : num >= min)) &&
        (_.isNil(max) || (excludeMax ? num < max : num <= max))) ||
        `输入值超出范围${excludeMin ? '(' : '['}${min ?? ''}, ${max ?? ''}${excludeMax ? ')' : ']'}`
      )
    }
  },
  rangeNumber (min, max, excludeMin, excludeMax) {
    if (min && typeof (min) === 'object' && !(min instanceof Number)) {
      const options = min
      min = options.min
      max = options.max
      excludeMin = options.excludeMin
      excludeMax = options.excludeMax
    }
    if (!_.isNaN(min) && Number.isNaN(min)) {
      throw new Error('指定的数字范围最小值无效:' + min)
    }
    if (!_.isNaN(max) && Number.isNaN(max)) {
      throw new Error('指定的数字范围最大值无效:' + max)
    }
    return function (val) {
      if (utils.isEmptyString(val)) return true
      const num = Number(val)
      if (Number.isNaN(num) || !Number.isFinite(num)) {
        return `输入值(${val})不是有效数字`
      }
      return ((
        (_.isNil(min) || (excludeMin ? num > min : num >= min)) &&
        (_.isNil(max) || (excludeMax ? num < max : num <= max))) ||
        `输入值超出范围${excludeMin ? '(' : '['}${min ?? ''}, ${max ?? ''}${excludeMax ? ')' : ']'}`
      )
    }
  },
  // 粒度 eg:
  // grain: 0.1; 1.5 -> true; 1.51 -> false
  // grain: 5; 15 -> true; 13 -> false; 15.5 -> false
  grained (grain) {
    if (_.isNil(grain)) {
      return function (val) { return true }
    } else if (Number.isNaN(grain) || !Number.isFinite) {
      throw new Error('指定的粒度无效:' + grain)
    } else {
      const exponentialGrain = grain.toExponential()
      const match = /^(\d+)(\.(\d+))?e([+-])(\d+)$/.exec(exponentialGrain)
      if (!match) {
        throw new Error(`无法识别指定的粒度(${grain})的科学计数法:${exponentialGrain}`)
      }
      const factorE = (match[3] ? match[3].length : 0) + (match[4] === '-' ? parseInt(match[5]) : 0)
      const factor = Math.pow(10, factorE)
      const intGrain = grain * factor
      return function (val) {
        if (utils.isEmptyString(val)) return true
        const num = Number(val)
        if (Number.isNaN(num) || !Number.isFinite(num)) {
          return `输入值(${val})不是有效数字`
        }
        if ((val * factor) % intGrain) {
          return `输入值(${val})不是 ${grain} 的整数倍`
        }
        return true
      }
    }
  },
  phone (val) {
    return utils.isEmptyString(val) || /\d{3,}/.test(val) || '请输入合法的电话号码'
  }
})
